/**
 * Shared "result verdict" wrapper that pairs LoopyBubble with a Next/Retry CTA.
 * Used by every learning-card component to present right/wrong feedback in a
 * way that matches the standalone learning modes (QuizPage, ShadowingPage).
 */
import React from 'react';
import { LoopyBubble } from '../loopy';
import type { LoopyState, LoopyEmotion } from '../loopy/LoopyAvatar';
import './LoopyVerdict.css';

export type VerdictKind = 'correct' | 'incorrect' | 'hint';

interface LoopyVerdictProps {
    kind: VerdictKind;
    title: string;
    message?: React.ReactNode;
    children?: React.ReactNode;
    nextLabel?: string;
    onNext?: () => void;
    retryLabel?: string;
    onRetry?: () => void;
    showKbdHint?: boolean;
    className?: string;
}

const stateFromKind = (kind: VerdictKind): LoopyState =>
    kind === 'correct' ? 'correct' : kind === 'hint' ? 'hint' : 'error';

const emotionFromKind = (kind: VerdictKind): LoopyEmotion =>
    kind === 'correct' ? 'celebrate' : kind === 'hint' ? 'wink' : 'pouting';

const LoopyVerdict: React.FC<LoopyVerdictProps> = ({
    kind,
    title,
    message,
    children,
    nextLabel,
    onNext,
    retryLabel,
    onRetry,
    showKbdHint = true,
    className = '',
}) => {
    return (
        <div className={`loopy-verdict loopy-verdict--${kind} ${className}`.trim()}>
            <LoopyBubble
                state={stateFromKind(kind)}
                emotion={emotionFromKind(kind)}
                title={title}
                message={message}
                className="loopy-verdict__bubble"
            >
                {children}
            </LoopyBubble>
            {(onNext || onRetry) && (
                <div className="loopy-verdict__actions">
                    {onRetry && (
                        <button type="button" className="loopy-verdict__btn loopy-verdict__btn--retry" onClick={onRetry}>
                            {retryLabel ?? 'Retry'}
                            {showKbdHint && <kbd className="loopy-verdict__kbd">Enter</kbd>}
                        </button>
                    )}
                    {onNext && (
                        <button type="button" className="loopy-verdict__btn loopy-verdict__btn--next" onClick={onNext}>
                            {nextLabel ?? 'Next'}
                            {showKbdHint && <kbd className="loopy-verdict__kbd">Enter</kbd>}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
};

export default LoopyVerdict;
